import type { Client } from '@frontend/api-types';
import { LoginHero } from '@frontend/assets';
import { match } from '@frontend/duck-tape';
import { useMCPCompleteAccountForm } from '@frontend/forms';
import { useAuthSlice } from '@frontend/redux';
import {
  AppShell,
  BirthdayInput,
  Button,
  ClerkLoading,
  ClerkUnavailable,
  Image,
  LoadingSpinnerPanel,
  PhoneNumberInput,
  ScrollableContentPanel,
  Text,
  Title,
  XStack,
  YStack,
  showToast,
  useSSOLoadState,
} from '@frontend/web-react';
import { UnauthenticatedAppHeader } from '@frontend/web/components';
import {
  useAnalytics,
  useAuthentication,
  useCreateMCPSocialAuthMutation,
  useEditProfileMutation,
  useMcpFlow,
} from '@frontend/web/hooks';
import { createFileRoute, handleMutation } from '@frontend/web/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

const MCP_ANALYTICS_BASE = {
  entryType: 'mcp',
  flowVariant: 'mcp',
} as const;

const MCPComplete = () => {
  const ssoLoadState = useSSOLoadState();
  const navigate = Route.useNavigate();
  const { storeToken } = useAuthSlice();
  const { sso } = useAuthentication();
  const { session } = sso;
  const { clearMcpFlow, isMcpFlow } = useMcpFlow();
  const { track } = useAnalytics();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [createError, setCreateError] = useState(false);
  const [existingUser, setExistingUser] = useState<Client | null>(null);
  const hasCreatedRef = useRef(false);

  const [createMCPSocialAuth] = useCreateMCPSocialAuthMutation();
  const [editProfile] = useEditProfileMutation();

  useEffect(() => {
    if (!isMcpFlow) return;
    track({
      data: { ...MCP_ANALYTICS_BASE, step: 'complete' },
      event: 'signupStepViewed',
      topic: 'auth',
    });
  }, [isMcpFlow, track]);

  useEffect(() => {
    if (!isMcpFlow) {
      navigate({ to: '/signup/mcp' });
    }
  }, [isMcpFlow, navigate]);

  useEffect(() => {
    if (!sso.isLoaded || session || hasCreatedRef.current) return;

    const timeoutId = setTimeout(() => {
      setIsCreatingAccount(false);
      setCreateError(true);
      track({
        data: {
          ...MCP_ANALYTICS_BASE,
          errorCategory: 'auth',
          errorCode: 'SESSION_TIMEOUT',
          errorSource: 'mcpAccountCreation',
          hasPromoCode: false,
          retriable: true,
        },
        event: 'signupSubmitFailed',
        topic: 'auth',
      });
    }, 10_000);

    return () => clearTimeout(timeoutId);
  }, [sso.isLoaded, session, track]);

  useEffect(() => {
    const createAccount = async () => {
      if (!sso.isLoaded || !session || hasCreatedRef.current) {
        return;
      }

      // eslint-disable-next-line fp/no-mutation
      hasCreatedRef.current = true;

      track({
        data: { ...MCP_ANALYTICS_BASE, hasPromoCode: false },
        event: 'signupSubmitStarted',
        topic: 'auth',
      });

      const clerkToken = await session.getToken({ template: 'backend-auth' });
      if (!clerkToken) {
        setIsCreatingAccount(false);
        track({
          data: {
            ...MCP_ANALYTICS_BASE,
            errorCategory: 'auth',
            errorCode: 'NO_SESSION_TOKEN',
            errorSource: 'mcpAccountCreation',
            hasPromoCode: false,
            retriable: true,
          },
          event: 'signupSubmitFailed',
          topic: 'auth',
        });
        showToast({ title: 'No session token found', variant: 'danger' });
        return;
      }

      try {
        const result = await createMCPSocialAuth({ clerkToken }).unwrap();
        storeToken(result.token);

        const hasBirthday = !!result.user.birthday;
        const hasPhone = !!result.user.phoneNumber;
        const isExistingUser = hasBirthday && hasPhone;

        track({
          data: {
            ...MCP_ANALYTICS_BASE,
            hasPromoCode: false,
          },
          event: 'signupSubmitSucceeded',
          topic: 'auth',
        });

        if (isExistingUser) {
          track({
            data: { hasPromoCode: false, hasReferrer: false, hasUtmParams: false },
            event: 'signupCompleted',
            topic: 'auth',
          });
          navigate({ to: '/signup/mcp/success' });
        } else {
          track({
            data: {
              ...MCP_ANALYTICS_BASE,
              needsBirthday: !hasBirthday,
              needsPhone: !hasPhone,
            },
            event: 'mcpProfileCompletionRequired',
            topic: 'auth',
          });
          setExistingUser(result.user);
          setShowProfileForm(true);
          setIsCreatingAccount(false);
        }
      } catch {
        setIsCreatingAccount(false);
        setCreateError(true);
        track({
          data: {
            ...MCP_ANALYTICS_BASE,
            errorCategory: 'unknown',
            errorCode: 'ACCOUNT_CREATION_FAILED',
            errorSource: 'mcpAccountCreation',
            hasPromoCode: false,
            retriable: true,
          },
          event: 'signupSubmitFailed',
          topic: 'auth',
        });
        showToast({ title: 'Failed to create account. Please try again.', variant: 'danger' });
      }
    };

    createAccount();
  }, [createMCPSocialAuth, navigate, session, sso.isLoaded, storeToken, track]);

  const handleFormSubmit = useCallback(
    async (formValues: { birthday: DateISOString; phoneNumber: string }) => {
      setIsSubmitting(true);

      track({
        data: { ...MCP_ANALYTICS_BASE },
        event: 'mcpProfileCompletionStarted',
        topic: 'auth',
      });

      await handleMutation({
        mutation: () =>
          editProfile({
            birthday: formValues.birthday,
            phoneNumber: formValues.phoneNumber,
          }).unwrap(),
        onComplete: () => setIsSubmitting(false),
        onError: () => {
          track({
            data: {
              ...MCP_ANALYTICS_BASE,
              errorCategory: 'validation',
              errorCode: 'PROFILE_UPDATE_FAILED',
              errorSource: 'mcpProfileCompletion',
              retriable: true,
            },
            event: 'mcpProfileCompletionFailed',
            topic: 'auth',
          });
        },
        onSuccess: () => {
          track({
            data: { ...MCP_ANALYTICS_BASE },
            event: 'mcpProfileCompletionSucceeded',
            topic: 'auth',
          });
          track({
            data: { hasPromoCode: false, hasReferrer: false, hasUtmParams: false },
            event: 'signupCompleted',
            topic: 'auth',
          });
          navigate({ to: '/signup/mcp/success' });
        },
        showSuccessToast: false,
      });
    },
    [editProfile, navigate, track],
  );

  const initialValues = {
    birthday: (existingUser?.birthday ?? '') as DateISOString,
    phoneNumber: existingUser?.phoneNumber ?? '',
  };

  const { dirty, getControl, handleSubmit, isValid } = useMCPCompleteAccountForm({
    initialValues,
    onSubmit: handleFormSubmit,
  });

  const needsBirthday = !existingUser?.birthday;
  const needsPhone = !existingUser?.phoneNumber;
  const canSubmit = isValid && (dirty || (!needsBirthday && !needsPhone)) && !isSubmitting;

  if (ssoLoadState.status !== 'success') {
    return (
      <AppShell>
        <UnauthenticatedAppHeader />
        <XStack className="h-screen">
          <ScrollableContentPanel>
            {match(ssoLoadState)
              .with({ status: 'loading' }, () => <ClerkLoading />)
              .with({ status: 'failure' }, ({ onReload }) => <ClerkUnavailable onAction={onReload} />)
              .exhaustive()}
          </ScrollableContentPanel>
          <YStack className="bg-surfaceBrand hidden flex-1 justify-center md:flex">
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <Image className="h-[500px] w-[960px]" source={LoginHero} />
          </YStack>
        </XStack>
      </AppShell>
    );
  }

  if (!sso.isLoaded || isCreatingAccount) {
    return <LoadingSpinnerPanel fullScreen />;
  }

  if (createError) {
    return (
      <AppShell>
        <UnauthenticatedAppHeader />
        <YStack alignItems="center" className="h-screen" gap="md" justifyContent="center">
          <Title type="h5">Something went wrong</Title>
          <Text color="secondary">We couldn&apos;t create your account. Please try again.</Text>
          <Button
            onClick={() => {
              clearMcpFlow();
              navigate({ to: '/signup/mcp' });
            }}
            variant="primary"
          >
            Try again
          </Button>
        </YStack>
      </AppShell>
    );
  }

  if (!showProfileForm) {
    return <LoadingSpinnerPanel fullScreen />;
  }

  return (
    <AppShell>
      <UnauthenticatedAppHeader />
      <XStack className="h-screen">
        <ScrollableContentPanel>
          <form className="gap-y-md flex flex-col" noValidate onSubmit={handleSubmit}>
            <Title type="h5">
              Hi{session?.user?.firstName ? `, ${session.user.firstName}` : ''}! Complete Your Account
            </Title>
            <Text color="secondary">Please provide the following information to complete your account setup.</Text>

            {needsPhone ? <PhoneNumberInput {...getControl('phoneNumber')} /> : null}

            {needsBirthday ? (
              <BirthdayInput
                error={getControl('birthday').error}
                onChange={(val) => getControl('birthday').onChange(val as DateISOString)}
                value={getControl('birthday').value}
              />
            ) : null}

            <Button className="mt-md" disabled={!canSubmit} fullWidth isForm isLoading={isSubmitting}>
              Complete Signup
            </Button>
          </form>
        </ScrollableContentPanel>
        <YStack className="bg-surfaceBrand hidden flex-1 justify-center md:flex">
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <Image className="h-[500px] w-[960px]" source={LoginHero} />
        </YStack>
      </XStack>
    </AppShell>
  );
};

export const Route = createFileRoute('/signup/mcp/complete')({
  component: MCPComplete,
});
